import React, { useState, useRef, useEffect } from "react";
import { Send, Smile, Paperclip, ChevronDown, X } from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import toast from "react-hot-toast";

function ChatCanvas({
  selectedTicket,
  messages = [],
  onStatusChange,
  onSendMessage,
  onEndChat,
  onChatEnded,
  loadingMessages
}) {
  
  const [message, setMessage] = useState("");
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState("");
  const [showLoader, setShowLoader] = useState(false);

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const statusDropdownRef = useRef(null);

  // Status options for dropdown
  const statusOptions = [
    {
      value: "In Progress",
      label: "In Progress",
      color: "#f59e0b",
      bgColor: "#fef3c7",
      icon: "◐",
      description:
        "This will indicate that work is actively being done on this ticket.",
    },
    {
      value: "Resolved",
      label: "Resolved",
      color: "#166534",
      bgColor: "#dcfce7",
      icon: "✓",
      description: "This will mark the ticket as completed and resolved.",
    },
  ];

  useEffect(() => {
  if (loadingMessages) {
    setShowLoader(true);
  } else {
    const t = setTimeout(() => setShowLoader(false), 1000); // fade out delay
    return () => clearTimeout(t);
  }
}, [loadingMessages]);

  const getCurrentStatusStyle = () => {
    const currentStatus = statusOptions.find(
      (s) => s.value === selectedTicket?.status
    );
    return currentStatus || statusOptions[0];
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  // Close status dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSendMessage = () => {
    if (!message.trim() || !selectedTicket) return;

    // UI-level send
    onSendMessage(message); // call parent

    // reset input
    setMessage("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEmojiClick = (emojiData) => {
    setMessage((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedTicket) return;

    // Temporary preview for instant UI feedback
    const tempPreview = URL.createObjectURL(file);

    // Send both file and preview up to parent
    onSendMessage({
      file,
      previewImage: file.type.startsWith("image/") ? tempPreview : null,
    });

    event.target.value = "";
  };

  const handleStatusDropdownClick = (newStatus) => {
    if (newStatus === selectedTicket.status) {
      setShowStatusDropdown(false);
      return;
    }

    setPendingStatus(newStatus);
    setShowStatusDropdown(false);
    setShowStatusModal(true);
  };

const handleConfirmStatusChange = async () => {
  if (!selectedTicket || !pendingStatus) return;

  try {

    if (onStatusChange) {
      onStatusChange(selectedTicket.id, pendingStatus);
    }


    if (pendingStatus === "Resolved" && onEndChat) {
      await onEndChat({ conversationID: selectedTicket.conversationID }).unwrap();

      // ✅ Notify parent that chat ended
      if (onChatEnded) onChatEnded();

      toast.success("Chat has been marked as resolved successfully!");
    } else {
      toast.success(`Status changed to ${pendingStatus}`);
    }

    // ✅ Close modal and clear temp state
    setShowStatusModal(false);
    setPendingStatus("");
  } catch (error) {
    console.error("❌ Failed to end chat:", error);
    toast.error("Failed to update chat status. Please try again.");
  }
};



  const handleCancelStatusChange = () => {
    setShowStatusModal(false);
    setPendingStatus("");
  };

  // Get user avatar class based on first letter
  const getUserAvatarClass = (clinicName) => {
    const firstLetter = clinicName?.charAt(0)?.toLowerCase() || "d";
    return `avatar-${firstLetter}`;
  };

  const getFirstLetter = (clinicName) => {
    return clinicName?.charAt(0)?.toUpperCase() || "D";
  };

  // Robust boolean coercion (handles true/false, "true"/"false", 1/0)
  const toBool = (v) => {
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v === 1;
    if (typeof v === "string") {
      const s = v.trim().toLowerCase();
      if (s === "true" || s === "1") return true;
      if (s === "false" || s === "0") return false;
    }
    return false;
  };

  // Decide side using API field isAdminResponse when present; fallback to isAdmin/sender for optimistic UI
  const getSide = (m) => {
    // Backend uses `sender` field → 'admin' or 'user'
    if (m?.sender) {
      const s = m.sender.toLowerCase();
      if (s === "admin") return "admin";
      if (s === "user") return "user";
    }

    // Backward compatibility
    if (m?.isAdminResponse !== undefined)
      return toBool(m.isAdminResponse) ? "admin" : "user";
    if (m?.isAdmin !== undefined) return toBool(m.isAdmin) ? "admin" : "user";

    return "user"; // default
  };

  const getDisplayText = (m) => m.message ?? m.text ?? "";
  const getDisplayTimestamp = (m) => m.createdAt ?? m.timestamp ?? "";

  if (!selectedTicket) {
    return (
      <div className="chat-canvas-container">
        <div className="no-ticket-selected">
          <h3>Select a ticket to start conversation</h3>
          <p>
            Choose a ticket from the left panel to begin handling the support
            request
          </p>
        </div>
      </div>
    );
  }

  // 🕒 Format date like "Friday, 12th Oct 2025"
  const formatDate = (isoString) => {
    if (!isoString) return "";

    const date = new Date(isoString);
    const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
    const day = date.getDate();

    const suffix =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
        ? "rd"
        : "th";

    const month = date.toLocaleDateString("en-US", { month: "short" });
    const year = date.getFullYear();

    return `${dayName}, ${day}${suffix} ${month} ${year}`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };
  

  const ChatCanvasStyles = `

  .loading-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: #e0f2fe; /* light blue info banner */
  color: #0369a1;
  font-weight: 500;
  padding: 8px 12px;
  font-size: 14px;
  animation: fadeInOut 2s ease-in-out;
  border-bottom: 1px solid #bae6fd;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #93c5fd;
  border-top: 2px solid #0369a1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
    transform: translateY(-5px);
  }
  15% {
    opacity: 1;
    transform: translateY(0);
  }
  85% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-5px);
  }
}


    .chat-canvas-container {
      height: 100%;
      display: flex;
      flex-direction: column;
      background: white;
      overflow: hidden;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
      position: relative;
    }

    .no-ticket-selected {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      color: #6b7280;
      padding: 24px;
    }

    .no-ticket-selected h3 {
      margin-bottom: 8px;
      color: #374151;
      font-weight: 500;
    }

    .chat-header {
      padding: 16px 20px;
      border-bottom: 1px solid #e5e5e5;
      background: white;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-shrink: 0;
    }

    .chat-user-info {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1;
    }

    .chat-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: 600;
      font-size: 16px;
    }

    /* User avatar colors matching ticket list */
    .avatar-d { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); }
    .avatar-j { background: linear-gradient(135deg, #ec4899 0%, #be185d 100%); }
    .avatar-b { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
    .avatar-r { background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%); }
    .avatar-k { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
    .avatar-s { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); }
    .avatar-m { background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%); }
    .avatar-e { background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); }
    .avatar-l { background: linear-gradient(135deg, #84cc16 0%, #65a30d 100%); }
    .avatar-a { background: linear-gradient(135deg, #ec4899 0%, #be185d 100%); }
    .avatar-c { background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%); }

    .chat-user-details h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: #1f2937;
    }

    .chat-user-details p {
      margin: 2px 0 0 0;
      font-size: 13px;
      color: #6b7280;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .status-indicator {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #10b981;
    }

    /* Status Dropdown */
    .status-dropdown-container {
      position: relative;
    }

    .status-dropdown-btn {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      border-radius: 6px;
      border: 1px solid #d1d5db;
      background: white;
      cursor: pointer;
      font-size: 13px;
      font-weight: 500;
      transition: all 0.15s ease;
      min-width: 120px;
      justify-content: space-between;
    }

    .status-dropdown-btn:hover {
      border-color: #9ca3af;
    }

    .status-label {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .status-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .dropdown-arrow {
      color: #6b7280;
      transition: transform 0.15s ease;
    }

    .dropdown-arrow.open {
      transform: rotate(180deg);
    }

    .status-dropdown {
      position: absolute;
      top: 100%;
      right: 0;
      margin-top: 4px;
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
      z-index: 1000;
      min-width: 180px;
      overflow: hidden;
    }

    .status-dropdown-option {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 12px;
      cursor: pointer;
      transition: background-color 0.15s ease;
      font-size: 14px;
    }

    .status-dropdown-option:hover {
      background-color: #f9fafb;
    }

    .status-dropdown-option.active {
      background-color: #eff6ff;
      color: #3b82f6;
    }

    /* Status Confirmation Modal */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
    }

    .modal-content {
      background: white;
      border-radius: 12px;
      padding: 8px;
      max-width: 422px;
      width: 23%;
      max-height: 80vh;
      overflow-y: auto;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
      position: relative;
    }

    .modal-close {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 24px;
      height: 24px;
      border: none;
      background: none;
      cursor: pointer;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #9ca3af;
      transition: all 0.15s ease;
    }

    .modal-close:hover {
      background-color: #f3f4f6;
      color: #374151;
    }

    .modal-header {
      padding: 0px 20px 0 20px;
    }

    .modal-header-content {
      display: flex;
      align-items: center;
    }

    .warning-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      margin-right: 16px;
      font-size: 22px;
      font-weight: 900;
    }

    .warning-icon.open {
      background: #dcedfc;
      color: #1573b8;
    }

    .warning-icon.in-progress {
      background: #FFF7E6;
      color: #FAAD14;
    }

    .warning-icon.resolved {
      background: #dcfce7;
      color: #166534;
    }

    .modal-title {
      font-weight: 600;
      font-size: 18px;
      color: #212529;
      margin: 0;
    }

    .modal-body {
      padding: 0 20px 8px 20px;
    }

    .modal-description {
      color: #5A5A5A;
      font-size: 15px;
      margin-bottom: 16px;
      padding-left: 2px;
      line-height: 1.5;
    }

    .modal-confirm-text {
      color: #d48806;
      font-size: 15px;
      padding-left: 2px;
    }

    .modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin-top: 40px;
    }

    .modal-btn {
      padding: 8px 28px;
      border-radius: 6px;
      font-weight: 500;
      font-size: 15px;
      cursor: pointer;
      transition: all 0.2s ease;
      border: none;
    }

    .modal-btn-cancel {
      border: 1px solid #e1e1e1;
      background: #fff;
      color: #333;
    }

    .modal-btn-cancel:hover {
      background-color: #f9fafb;
    }

    .modal-btn-confirm {
      border: none;
      box-shadow: 0 1px 6px #e5e5e5;
      font-weight: 600;
    }

    .modal-btn-confirm.open {
      background: #1573b8;
      color: white;
    }

    .modal-btn-confirm.in-progress {
      background: #ffc53d;
      color: #402700;
    }

    .modal-btn-confirm.resolved {
      background: #16a34a;
      color: white;
    }

    .modal-btn-confirm:hover {
      transform: translateY(-1px);
    }

    .chat-messages {
      flex: 1;
      padding: 20px;
      overflow-y: auto;
      overflow-x: hidden;
      background: #fefefe;
      display: flex;
      flex-direction: column;
      gap: 16px;
      height: 0;
      -ms-overflow-style: none;
      scrollbar-width: none;
    }

    .chat-messages::-webkit-scrollbar {
      display: none;
    }

    .date-separator {
      text-align: center;
      color: #9ca3af;
      font-size: 13px;
      font-weight: 500;
      margin: 20px 0;
      position: relative;
    }

    .date-separator::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      height: 1px;
      background: #f3f4f6;
      z-index: 1;
    }

    .date-separator span {
      background: #fefefe;
      padding: 0 16px;
      position: relative;
      z-index: 2;
    }

    .message-group {
      display: flex;
      gap: 12px;
      align-items: flex-start;
      margin-bottom: 2px;
      width: 100%;
    }

    .message-group.admin {
      flex-direction: row-reverse;
    }

    .message-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: 600;
      font-size: 12px;
      flex-shrink: 0;
    }

    .admin-avatar {
      background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
    }

    .message-content {
      flex: 1;
      max-width: calc(100% - 80px);
      display: flex;
      flex-direction: column;
    }

    .message-group.admin .message-content {
      align-items: flex-end;
    }

    .message-group:not(.admin) .message-content {
      align-items: flex-start;
    }

    .message-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 4px;
      font-size: 13px;
    }

    .message-group.admin .message-header {
      justify-content: flex-end;
      flex-direction: row-reverse;
    }

    .message-name {
      font-weight: 600;
      color: #374151;
    }

    .message-timestamp {
      color: #9ca3af;
      font-size: 12px;
    }

    .message-bubble {
      display: inline-block;
      max-width: 74%;
      min-width: 60px;
      padding: 12px 16px;
      font-size: 14px;
      line-height: 1.4;
      white-space: pre-wrap;
      word-wrap: break-word;
      border-radius: 18px 18px 18px 4px;
      background-color: #ffffff;
      color: #374151;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      border: 1px solid #e5e7eb;
      position: relative;
    }

    .message-group.admin .message-bubble {
      background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
      border-radius: 18px 18px 4px 18px;
      color: #ffffff;
      border-color: #3b82f6;
      box-shadow: 0 1px 3px rgba(59, 130, 246, 0.2);
    }

    .message-attachment {
      margin-top: 8px;
      padding: 12px;
      background: rgba(255, 255, 255, 0.9);
      border-radius: 8px;
      display: flex;
      align-items: center;
      gap: 10px;
      cursor: pointer;
      transition: background-color 0.15s ease;
      max-width: 320px;
    }

    .message-group.admin .message-attachment {
      background: #304f6026;
    }

    .message-attachment:hover {
      background: rgba(255, 255, 255, 1);
    }

    .attachment-preview {
      width: 40px;
      height: 40px;
      background: #e2e8f0;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      color: #64748b;
      flex-shrink: 0;
    }

    .attachment-info {
      flex: 1;
      min-width: 0;
    }

    .attachment-info h4 {
      margin: 0;
      font-size: 13px;
      font-weight: 500;
      color: #1e293b;
      word-break: break-word;
    }

    .attachment-info p {
      margin: 2px 0 0 0;
      font-size: 12px;
      color: #64748b;
      word-break: break-all;
    }

    .typing-indicator {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 0;
      color: #6b7280;
      font-size: 13px;
    }

    .typing-dots {
      display: flex;
      gap: 4px;
      align-items: center;
    }

    .typing-dot {
      width: 6px;
      height: 6px;
      background: #9ca3af;
      border-radius: 50%;
      animation: typing 1.4s infinite;
    }

    .typing-dot:nth-child(2) { animation-delay: 0.2s; }
    .typing-dot:nth-child(3) { animation-delay: 0.4s; }

    @keyframes typing {
      0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
      30% { opacity: 1; transform: translateY(-4px); }
    }

    .chat-input-container {
      border-top: 1px solid #e5e5e5;
      padding: 16px 20px;
      background: white;
      position: relative;
      flex-shrink: 0;
    }

    .emoji-picker-container {
      position: absolute;
      bottom: 70px;
      right: 20px;
      z-index: 1000;
      transform-origin: bottom right;
    }

    .chat-input-wrapper {
      display: flex;
      align-items: flex-end;
      gap: 8px;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 22px;
      padding: 8px 12px;
      transition: all 0.15s ease;
    }

    .chat-input-wrapper:focus-within {
      border-color: #3b82f6;
      background: white;
    }

    .chat-input {
      flex: 1;
      border: none;
      background: none;
      outline: none;
      font-size: 14px;
      color: #374151;
      resize: none;
      min-height: 20px;
      max-height: 100px;
      padding: 6px 0;
      font-family: inherit;
      box-shadow: none !important;
    }

    .chat-input:focus {
      border: none !important;
      outline: none !important;
      box-shadow: none !important;
    }

    .chat-input::placeholder {
      color: #9ca3af;
    }

    .input-actions {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .input-action-btn {
      width: 28px;
      height: 28px;
      border: none;
      background: none;
      cursor: pointer;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #6b7280;
      transition: all 0.15s ease;
    }

    .input-action-btn:hover {
      background-color: #f3f4f6;
      color: #374151;
    }

    .input-action-btn:focus {
      outline: none;
      box-shadow: none;
    }

    .send-btn {
      width: 32px;
      height: 32px;
      background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
      color: white;
      border: none;
      border-radius: 50%;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s ease;
      opacity: ${message.trim() ? "1" : "0.5"};
    }

    .send-btn:hover {
      transform: scale(1.05);
    }

    .send-btn:disabled {
      cursor: not-allowed;
      transform: none;
    }

    .send-btn:focus {
      outline: none;
      box-shadow: none;
    }

    /* Remove all focus outlines and box shadows */
    * {
      -webkit-tap-highlight-color: transparent;
    }

    *:focus {
      outline: none !important;
      box-shadow: none !important;
    }

    button:focus,
    textarea:focus,
    input:focus {
      outline: none !important;
      box-shadow: none !important;
      border-color: inherit !important;
    }
  `;

  return (
    <>
      <style>{ChatCanvasStyles}</style>
      <div className="chat-canvas-container">
        {/* Chat Header */}
        <div className="chat-header">
          <div className="chat-user-info">
            <div
              className={`chat-avatar ${getUserAvatarClass(
                selectedTicket.clinicName
              )}`}
            >
              {getFirstLetter(selectedTicket.clinicName)}
            </div>
            <div className="chat-user-details">
              <h3>{selectedTicket.clinicName || selectedTicket.email}</h3>
              <p>
                <span className="status-indicator"></span>
                {selectedTicket.status} • {selectedTicket.clinicID} •{" "}
                {selectedTicket.conversationID}
              </p>
            </div>
          </div>

          {/* Status Dropdown */}
          <div className="status-dropdown-container" ref={statusDropdownRef}>
            <button
              className="status-dropdown-btn"
              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
            >
              <div className="status-label">
                <div
                  className="status-dot"
                  style={{ backgroundColor: getCurrentStatusStyle().color }}
                ></div>
                {selectedTicket.status}
              </div>
              <ChevronDown
                size={16}
                className={`dropdown-arrow ${showStatusDropdown ? "open" : ""}`}
              />
            </button>

            {showStatusDropdown && (
              <div className="status-dropdown">
                {statusOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`status-dropdown-option ${
                      selectedTicket.status === option.value ? "active" : ""
                    }`}
                    onClick={() => handleStatusDropdownClick(option.value)}
                  >
                    <div
                      className="status-dot"
                      style={{ backgroundColor: option.color }}
                    ></div>
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Status Confirmation Modal */}
        {showStatusModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <button
                className="modal-close"
                onClick={handleCancelStatusChange}
              >
                <X size={16} />
              </button>

              <div className="modal-header">
                <div className="modal-header-content">
                  <div
                    className={`warning-icon ${pendingStatus
                      ?.toLowerCase()
                      .replace(" ", "-")}`}
                  >
                    {pendingStatus === "Resolved" ? "✓" : "◐"}
                  </div>
                  <h2 className="modal-title">Mark as {pendingStatus}?</h2>
                </div>
              </div>

              <div className="modal-body">
                <div className="modal-description">
                  {pendingStatus === "Resolved"
                    ? "This will mark the ticket as completed and resolved."
                    : "This will indicate that work is actively being done on this ticket."}
                </div>
                <div className="modal-confirm-text">
                  Please confirm to proceed.
                </div>

                <div className="modal-actions">
                  <button
                    className="modal-btn modal-btn-cancel"
                    onClick={handleCancelStatusChange}
                  >
                    Cancel
                  </button>
                  <button
                    className={`modal-btn modal-btn-confirm ${pendingStatus
                      ?.toLowerCase()
                      .replace(" ", "-")}`}
                    onClick={handleConfirmStatusChange}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Messages Area */}
        <div className="chat-messages">

          {showLoader && (
  <div className="loading-banner">
    <div className="spinner"></div>
    <span>Fetching messages…</span>
  </div>
)}

          <div className="date-separator">
            <span>{formatDate(selectedTicket?.createdAt)}</span>
          </div>

          {messages.map((msg, index) => {
            const side = getSide(msg);
            // console.log(
            //   "Message:",
            //   msg.message || msg.text,
            //   "→ isAdminResponse:",
            //   msg.isAdminResponse,
            //   "→ Side:",
            //   side,
            //   "→ Type:",
            //   typeof msg.isAdminResponse
            // );

            // console.log("Fetched message object:", msg);
            const prevSide =
              index > 0 ? getSide(messages[index - 1]) : undefined;
            const nextSide =
              index < messages.length - 1
                ? getSide(messages[index + 1])
                : undefined;

            const showAvatar = index === 0 || prevSide !== side;
            const isLastInGroup =
              index === messages.length - 1 || nextSide !== side;

            return (
              <div key={msg.id ?? index} className={`message-group ${side}`}>
                {showAvatar && (
                  <div
                    className={`message-avatar ${
                      side === "admin"
                        ? "admin-avatar"
                        : getUserAvatarClass(
                            msg.clinicName || selectedTicket.clinicName
                          )
                    }`}
                  >
                    {side === "admin"
                      ? "Y"
                      : getFirstLetter(
                          msg.clinicName || selectedTicket.clinicName
                        )}
                  </div>
                )}

                {!showAvatar && <div style={{ width: "32px" }} />}

                <div className="message-content">
                  {showAvatar && (
                    <div className="message-header">
                      <span className="message-name">
                        {side === "admin"
                          ? "You"
                          : msg.clinicName || selectedTicket.clinicName}
                      </span>
                      <span className="message-timestamp">
                        {formatDate(getDisplayTimestamp(msg))}
                      </span>
                    </div>
                  )}

                  <div className="message-bubble">
                    <div>{getDisplayText(msg)}</div>

                    {msg.hasAttachment && (
                      <div className="message-attachment">
                        {msg.attachmentType === "image" &&
                        (msg.previewImage || msg.fileUrl) ? (
                          <img
                            src={msg.previewImage || msg.fileUrl}
                            alt={msg.fileName || msg.text}
                            style={{
                              maxWidth: "220px",
                              borderRadius: "10px",
                              cursor: "pointer",
                              marginTop: "6px",
                            }}
                            onClick={() =>
                              window.open(
                                msg.fileUrl || msg.previewImage,
                                "_blank"
                              )
                            }
                          />
                        ) : (
                          <div
                            className="attachment-file"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              backgroundColor: "#f9fafb",
                              padding: "8px 10px",
                              borderRadius: "8px",
                              border: "1px solid #e5e7eb",
                              marginTop: "6px",
                            }}
                          >
                            <span>📎</span>
                            <a
                              href={msg.fileUrl || msg.previewImage}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#2563eb",
                                textDecoration: "underline",
                                wordBreak: "break-all",
                              }}
                            >
                              {msg.fileName || msg.text}
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    {/* ⏰ show message time below bubble */}
                    <div
                      style={{
                        fontSize: "11px",
                        color: side === "admin" ? "#fff" : "#6b7280",
                        marginTop: "4px",
                        textAlign: "right",
                      }}
                    >
                      {formatTime(msg.createdAt || msg.timestamp)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {isTyping && (
            <div className="typing-indicator">
              <div
                className={`message-avatar ${getUserAvatarClass(
                  selectedTicket.name
                )}`}
              >
                {getFirstLetter(selectedTicket.name)}
              </div>
              <div>
                <span>{selectedTicket.name || "User"} is typing</span>
                <div className="typing-dots">
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="chat-input-container">
          {showEmojiPicker && (
            <div className="emoji-picker-container">
              <EmojiPicker
                onEmojiClick={handleEmojiClick}
                width={320}
                height={400}
                searchPlaceholder="Search emojis..."
                previewConfig={{ showPreview: false }}
                skinTonesDisabled={false}
                theme="light"
                emojiStyle="apple"
              />
            </div>
          )}

          <div className="chat-input-wrapper">
            <textarea
              ref={inputRef}
              className="chat-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={
                selectedTicket?.status === "Resolved"
                  ? "This ticket has been resolved. Messaging is disabled."
                  : "Send your message..."
              }
              rows="1"
              disabled={selectedTicket?.status === "Resolved"}
              style={{
                height: "auto",
                minHeight: "20px",
                maxHeight: "100px",
                backgroundColor:
                  selectedTicket?.status === "Resolved" ? "#f3f4f6" : "white",
                color:
                  selectedTicket?.status === "Resolved" ? "#9ca3af" : "#111827",
                cursor:
                  selectedTicket?.status === "Resolved"
                    ? "not-allowed"
                    : "text",
              }}
              onInput={(e) => {
                e.target.style.height = "auto";
                e.target.style.height =
                  Math.min(e.target.scrollHeight, 100) + "px";
              }}
            />

            <div className="input-actions">
              <button
                className="input-action-btn"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                title="Add emoji"
              >
                <Smile size={16} />
              </button>

              <button
                className="input-action-btn"
                onClick={() => fileInputRef.current?.click()}
                title="Attach file"
              >
                <Paperclip size={16} />
              </button>

              <button
                className="send-btn"
                onClick={handleSendMessage}
                disabled={!message.trim()}
                title="Send message"
              >
                <Send size={14} />
              </button>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileUpload}
            style={{ display: "none" }}
            accept="image/*,application/pdf,.doc,.docx"
          />
        </div>
      </div>
    </>
  );
}

export default ChatCanvas;
